<template>
	<div class="top-titles-hero" v-if="title">
		<div class="top-titles-hero__bg">
			<div class="top-titles-hero__bg_back"></div>
			<PictureComp
				v-if="title && backdropUrl"
				:key="`backdrop-${title.id}`"
				class="title-poster__image"
				:alt="title.content.title"
				imageProfile="backdrop"
				:imageUrl="backdropUrl"
				:slug="'Poster'"
				:lazy="false"
			/>
		</div>
		<div class="top-titles-hero__header hero-full container-fluid container-max-width">
			<h1>Top 10 in {{ countryName }} today</h1>
			<span class="top-titles-hero__header__title">
				{{ title.content.title }}
			</span>

			<span class="top-titles-hero__header__subtitle">
				{{ title.content.shortDescription }}
			</span>
			<div class="top-titles-hero__header__buttons">
				<WatchNowButton
					v-if="watchNowOffer"
					:key="`watch-now-${title.id}`"
					class="watch-now-button"
					color="primary"
					:titleObjectId="title.objectId"
					:titleObjectType="title.objectType"
					:title="title"
					:watchNowOffer="watchNowOffer"
					size="large"
					:additionalContexts="additionalContexts"
				/>

				<RouterLink v-if="title?.content?.fullPath" :to="title.content.fullPath">
					<BasicButton size="large" @click="$emit('titleClicked')" color="dark" class="more-info">
						{{ $t('WEBAPP_SHOW_MORE_TEXT') }}
					</BasicButton>
				</RouterLink>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { thumborFilter, ThumborOptions } from '@/filters/thumbor';
import { computed } from 'vue';
import WatchNowButton from '@/components/buttons/WatchNowButton.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { Viewport } from '@/enums/viewport';
import type { Top10ModuleInYourCountry } from '@/features/onboarded-home/types';

type TitleType = Top10ModuleInYourCountry['titles'][number];
const props = defineProps<{
	title: TitleType;
	additionalContexts: SnowplowContext[];
}>();

defineEmits<{
	(e: 'titleClicked'): void;
}>();

const { t } = useI18n();
const languageStore = useLanguageStore();
useProvideClickoutPlacement('home_top_free_title');

const countryName = computed(() => {
	return t('WEBAPP_COUNTRY_' + languageStore.country);
});

const backdropUrl = computed(() => {
	const posterUrl = props.title.content.backdrops[0]?.backdropUrl;
	if (!posterUrl) return '';
	return getImageUrl(posterUrl);
});

const watchNowOffer = computed(() => {
	return props.title.watchNowOffer;
});

function getImageUrl(posterUrl: string | undefined | null) {
	return thumborFilter()({
		url: posterUrl,
		imageType: 'backdrop',
		// FORCING THE HIGHEST QUALITY IMAGE FOR HERO BACKDROPS
		forceHighQuality: true,
		currentViewport: import.meta.client ? undefined : Viewport.DESKTOP,
	} as ThumborOptions);
}
</script>

<style lang="scss" scoped>
.top-titles-hero {
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	align-items: center;
	gap: 64px;
	width: 100%;
	position: relative;
	overflow: hidden;
	margin-top: 0px;
	min-height: 500px;
	padding-bottom: 40px;
	@media screen and (max-width: 1050px) {
		padding-bottom: 0px;
		justify-content: flex-end;
	}
	&__bg {
		position: absolute;
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		z-index: 0;
		@media screen and (max-width: 1050px) {
			inset-inline: -15px;
		}

		:deep(img) {
			object-position: center top;
		}

		&_back {
			position: absolute;
			left: 0;
			right: 0;
			top: -3px;
			bottom: -3px;
			z-index: 1;
			background: linear-gradient(270deg, rgba(6, 13, 23, 0) 0%, #060d17 92%),
				linear-gradient(180deg, rgba(6, 13, 23, 0) 21.88%, #060d17 96.07%);
		}
	}
	> * {
		z-index: 1;
	}
	&__header {
		@media screen and (max-width: 1050px) {
			// padding: 0 5vw 20px 5vw;
			padding: 0 0 20px 0;
		}
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: flex-start;
		gap: 16px;
		// padding: 0 7.5vw;
		width: 100%;

		h1 {
			color: #fff;
			-webkit-font-smoothing: antialiased;
			font-family: Anton, Anton-Fallback, Impact;
			font-size: 22px;
			font-style: normal;
			font-weight: 400;
			padding: 0;
			margin: 0;
		}
		&__title {
			color: #fff;
			-webkit-font-smoothing: antialiased;
			font-family: Anton, Anton-Fallback, Impact;
			font-size: 40px;
			font-style: normal;
			font-weight: 400;
			line-height: 48px; /* 120% */
			letter-spacing: 0.8px;
			padding: 0;
			margin: 0;
			@media screen and (min-width: 1240px) {
				max-width: 480px;
			}
			@media screen and (max-width: 1240px) {
				text-align: start;
				font-size: 32px;
			}
		}
		&__buttons {
			display: flex;
			flex-direction: row;
			gap: 8px;

			:deep(.basic-button) {
				padding: 0;
				border-radius: 8px;
			}

			:deep(.basic-button__content) {
				font-weight: 900 !important;
				font-size: 16px !important;
				font-style: normal !important;
				padding: 8px 24px;
			}

			:deep(.watch-now-button-contents) {
				gap: 8px;
			}

			.more-info {
				height: 100%;
			}
		}

		&__subtitle {
			color: var(--text-text-medium, #d2d3d5);
			font-size: 16px;
			display: -webkit-box;
			line-clamp: 2;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			text-overflow: ellipsis;
			@media screen and (min-width: 1050px) {
				width: 50%;
			}
		}
	}
}
</style>
